<template>
  <div>
    <CommentInner
      class="border-b border-black/15 pb-4 last-of-type:border-none"
      :comment="comment"
      :is-sending="isSending"
      :is-replies-fetching="repliesStates[comment.id as string]?.isFetching"
      :is-reply-form-open="repliesStates[comment.id as string]?.isOpen"
      :is-report-form-open="reportStates[comment.id as string]?.isOpen"
      @set-reply-form="
        setReplyForm(comment.id, comment.name, comment.comment, comment.replyCount, comment.id)
      "
      @set-report-form="setReportForm(comment.id)"
      @clear-replies="clearReplyForm(comment.id as string)"
      @set-edit-form="setEditForm"
    />
    <transition name="fade" mode="out-in">
      <div
        v-if="replies && repliesStates[comment.id as string]?.isOpen"
        class="grid gap-y-4 border-t border-black/10 py-4 pl-8 last-of-type:border-none"
      >
        <div v-for="repliedComment in replies" :key="repliedComment.id">
          <CommentInner
            class="border-b border-black/10 pb-4"
            :comment="repliedComment"
            is-replied-comment
            :is-sending="isSending"
            :is-replies-fetching="repliesStates[repliedComment.id as string]?.isFetching"
            :is-report-form-open="reportStates[repliedComment.id as string]?.isOpen"
            @set-report-form="setReportForm(repliedComment.id)"
            @set-reply-form="
              setReplyForm(
                repliedComment.id,
                repliedComment.name,
                repliedComment.comment,
                repliedComment.replyCount,
                comment.id,
              )
            "
            @set-edit-form="setEditForm"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useCommentStore } from '@stores/comment.store';

// Interfaces
import type { CommentInterface } from '@modules/Comment/comment.interface';

// Components
import CommentInner from '@modules/Comment/CommentInner.vue';

// Props
const props = defineProps<{
  comment: CommentInterface;
  contentId?: string;
  comments?: CommentInterface[];
}>();

// Refs
const replies = ref<CommentInterface[] | null>(null);
const isSending = ref(false);
const repliesStates = ref<Record<string, { isFetching: boolean; isOpen: boolean }>>({});
const reportStates = ref<Record<string, { isOpen: boolean }>>({});

// Stores
const commentStore = useCommentStore();

// Watchers
watch(
  () => props.comments,
  async (value) => {
    await fetchReplies();

    if (repliesStates.value[props.comment.id as string])
      repliesStates.value[props.comment.id as string].isOpen = true;

    replies.value = value ?? [];
  },
);

// Methods
const showRepliedComments = (id: string) => {
  if (!repliesStates.value[id]) {
    repliesStates.value[id] = { isFetching: false, isOpen: false };
  }
  repliesStates.value[id].isOpen = true;
};

const clearReplyForm = async (id: string) => {
  await commentStore.clearReplyData();

  if (repliesStates.value[id]) {
    repliesStates.value[id].isOpen = false;
  }
  if (id === props.comment.id) {
    replies.value = null;
  }
};

const setReplyForm = async (
  replyToId: CommentInterface['replyToId'],
  name: CommentInterface['name'],
  comment: CommentInterface['comment'],
  replyCount: CommentInterface['replyCount'],
  parentId: CommentInterface['id'],
) => {
  const replyId = String(replyToId);

  // Ensure state object exists for this reply
  if (!repliesStates.value[replyId]) {
    repliesStates.value[replyId] = { isFetching: false, isOpen: false };
  }

  // Show replied comments for the main comment
  showRepliedComments(props.comment.id as string);

  // Store reply data in the comment store
  commentStore.storeReplyData(String(parentId) || replyId, String(name), String(comment));
  commentStore.setReportCommentForm(false);

  // Fetch replies if needed, otherwise close the reply form
  if (replyCount && replyCount > 0 && !replies.value) {
    repliesStates.value[replyId].isFetching = true;
    await fetchReplies();
    repliesStates.value[replyId].isFetching = false;
  } else {
    repliesStates.value[replyId].isOpen = false;
  }
};

const setReportForm = (id?: string) => {
  if (!id) return;

  if (!reportStates.value[id]) {
    reportStates.value[id] = { isOpen: false };
  }
  reportStates.value[id].isOpen = !reportStates.value[id].isOpen;
};

const fetchReplies = async () => {
  await commentStore.fetchComments(props.contentId, props.comment.id);
  replies.value = commentStore.commentData;
};

const setEditForm = (
  replyToId: CommentInterface['replyToId'],
  name: CommentInterface['name'],
  comment: CommentInterface['comment'],
) => {
  commentStore.storeReplyData(String(replyToId), String(name), String(comment));
  commentStore.storeEditData(name as string, comment as string);
  console.log('kalsdkj');
  // commentStore.fetchComments(props.contentId, props.comment.id);
};
</script>
